import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Grid, Input, theme, Typography } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import { CreateTournamentInput, Tournament } from "../../../services/beerpongService";
import TournamentForm from "./TournamentForm";
import TournamentList from "./TournamentList";
import TournamentDetails from "./TournamentDetails";
import { DEFAULT_STYLE } from "./constants";
import { useTournamentForm } from "./hooks/useTournamentForm";
import { useTournamentManager } from "./hooks/useTournamentManager";
import {TournamentVisibility} from "../../../generated/bpong_pb";
import { useTranslation } from "react-i18next";
import "./beerpong.css";
import { useAppFeedback } from "../../useAppFeedback";

const { Title, Paragraph, Text } = Typography;
const { useBreakpoint } = Grid;

const TournamentManagerPage: React.FC = () => {
    const { message } = useAppFeedback();
    const navigate = useNavigate();
    const { isAuthenticated, user } = useAuth();
    const screens = useBreakpoint();
    const { token } = theme.useToken();
    const { t } = useTranslation();
    const [searchTerm, setSearchTerm] = useState<string>("");
    const [selectedTournamentId, setSelectedTournamentId] = useState<number | null>(null);

    const {
        tournamentName,
        teams,
        selectedStyle,
        selectedVisibility,
        accessPassword,
        setTournamentName,
        setSelectedStyle,
        setSelectedVisibility,
        setAccessPassword,
        addTeam,
        removeTeam,
        updateTeamName,
        addParticipant,
        updateParticipant,
        removeParticipant,
        resetForm,
        validTeams,
    } = useTournamentForm(DEFAULT_STYLE);

    const {
        tournaments,
        loadingTournaments,
        creatingTournament,
        updatingMatchId,
        updatingTournamentId,
        unlockingTournamentId,
        eliminatingTeamId,
        advancingTournamentId,
        restoringTeamId,
        loadTournaments,
        createTournamentEntry,
        updateMatchWinner,
        updateMatchTeams,
        updateTournamentStatus,
        updateTournamentStyle,
        updateTournamentVisibility,
        eliminateTournamentTeam,
        restoreTournamentTeam,
        advanceTournamentBracket,
        unlockTournamentDetails,
        deletingTournamentId,
        deleteTournamentEntry,
    } = useTournamentManager(user);

    const handleCreateTournament = useCallback(async () => {
        if (!isAuthenticated || !user) {
            message.warning(t("beerpong.tournament.messages.signInRequired"));
            return;
        }

        const trimmedName = tournamentName.trim();
        if (!trimmedName) {
            message.error(t("beerpong.tournament.messages.nameRequired"));
            return;
        }

        if (validTeams.length < 2) {
            message.error(t("beerpong.tournament.messages.minTeams"));
            return;
        }

        const teamWithoutParticipants = validTeams.find((team) => team.participants.length === 0);
        if (teamWithoutParticipants) {
            message.error(
                t("beerpong.tournament.messages.teamNeedsParticipant", {
                    team: teamWithoutParticipants.teamName,
                })
            );
            return;
        }

        if (
            selectedVisibility === TournamentVisibility.TOURNAMENT_VISIBILITY_PASSWORD &&
            accessPassword.trim().length === 0
        ) {
            message.error(t("beerpong.tournament.messages.passwordRequired"));
            return;
        }

        const hasByeTeam = validTeams.length % 2 === 1;

        const password =
            selectedVisibility === TournamentVisibility.TOURNAMENT_VISIBILITY_PASSWORD
                ? accessPassword.trim()
                : null;

        const input: CreateTournamentInput = {
            tournamentName: trimmedName,
            teams: validTeams.map((team) => ({
                teamName: team.teamName,
                participants: team.participants.map((participant) => ({
                    displayName: participant,
                })),
            })),
            style: selectedStyle,
            visibility: selectedVisibility,
            password: password ?? undefined,
        };

        const success = await createTournamentEntry(input, user, hasByeTeam);
        if (success) {
            resetForm();
            setAccessPassword("");
        }
    }, [
        createTournamentEntry,
        isAuthenticated,
        accessPassword,
        resetForm,
        selectedStyle,
        selectedVisibility,
        tournamentName,
        user,
        t,
        validTeams,
        setAccessPassword,
        message,
    ]);

    const handleDeleteTournament = useCallback(
        async (tournament: Tournament) => {
            await deleteTournamentEntry(tournament);
            if (selectedTournamentId === tournament.tournamentId) {
                setSelectedTournamentId(null);
            }
        },
        [deleteTournamentEntry, selectedTournamentId]
    );

    useEffect(() => {
        if (
            selectedVisibility !== TournamentVisibility.TOURNAMENT_VISIBILITY_PASSWORD &&
            accessPassword
        ) {
            setAccessPassword("");
        }
    }, [selectedVisibility, accessPassword, setAccessPassword]);

    const filteredTournaments = useMemo(() => {
        const normalized = searchTerm.trim().toLowerCase();
        if (!normalized) {
            return tournaments;
        }
        return tournaments.filter((tournament) =>
            tournament.tournamentName.toLowerCase().includes(normalized)
        );
    }, [searchTerm, tournaments]);

    useEffect(() => {
        if (filteredTournaments.length === 0) {
            setSelectedTournamentId(null);
            return;
        }

        const containsSelected = filteredTournaments.some(
            (tournament) => tournament.tournamentId === selectedTournamentId
        );

        if (!containsSelected) {
            setSelectedTournamentId(filteredTournaments[0].tournamentId);
        }
    }, [filteredTournaments, selectedTournamentId]);

    const selectedTournament =
        tournaments.find((tournament) => tournament.tournamentId === selectedTournamentId) ?? null;
    const tournamentDetails = (
        <TournamentDetails
            tournament={selectedTournament}
            updatingMatchId={updatingMatchId}
            updatingTournamentId={updatingTournamentId}
            unlockingTournamentId={unlockingTournamentId}
            eliminatingTeamId={eliminatingTeamId}
            advancingTournamentId={advancingTournamentId}
            restoringTeamId={restoringTeamId}
            onMatchWinnerChange={updateMatchWinner}
            onMatchTeamsChange={updateMatchTeams}
            onTournamentStatusChange={updateTournamentStatus}
            onTournamentStyleChange={updateTournamentStyle}
            onTournamentVisibilityChange={updateTournamentVisibility}
            onEliminateTeam={eliminateTournamentTeam}
            onRestoreTeam={restoreTournamentTeam}
            onAdvanceTournament={advanceTournamentBracket}
            onUnlockTournament={unlockTournamentDetails}
            deletingTournamentId={deletingTournamentId}
            onDeleteTournament={handleDeleteTournament}
            screens={screens}
            currentUserId={user?.userId ?? null}
        />
    );

    return (
        <div className="bp-manager">
            <header className="bp-page-heading">
                <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate("/beerpong")}>
                    {t("beerpong.tournament.backButton")}
                </Button>
                <Title level={1}>{t("beerpong.tournament.title")}</Title>
            </header>
            <div className="bp-manager-grid">
                <aside className="bp-tournament-sidebar" aria-labelledby="bp-tournament-list-heading">
                    <div className="bp-list-heading">
                        <Title id="bp-tournament-list-heading" level={2}>{t("beerpong.tournament.sections.listTitle")}</Title>
                    </div>
                    <Input.Search
                        allowClear
                        placeholder={t("beerpong.tournament.searchPlaceholder")}
                        value={searchTerm}
                        onChange={(event) => setSearchTerm(event.target.value)}
                    />
                    <TournamentList
                        tournaments={filteredTournaments}
                        loading={loadingTournaments}
                        onReload={loadTournaments}
                        selectedTournamentId={selectedTournamentId}
                        onSelectTournament={(tournament) => setSelectedTournamentId(tournament.tournamentId)}
                        token={token}
                    />
                </aside>
                <section className="bp-tournament-detail" aria-label={`${t("beerpong.tournament.sections.detailsDrawerTitle")}: ${selectedTournament?.tournamentName ?? t("beerpong.tournament.details.empty")}`}>
                    {tournamentDetails}
                </section>
            </div>
            <section className="bp-create-section" aria-labelledby="bp-tournament-setup-heading">
                <div className="bp-create-intro">
                    <Title id="bp-tournament-setup-heading" level={3}>{t("beerpong.tournament.sections.setupTitle")}</Title>
                    <Paragraph>{t("beerpong.tournament.sections.setupDescription")}</Paragraph>
                    {!isAuthenticated && <Text type="secondary">{t("beerpong.tournament.signInNotice")}</Text>}
                </div>
                <TournamentForm
                    tournamentName={tournamentName}
                    selectedStyle={selectedStyle}
                    selectedVisibility={selectedVisibility}
                    accessPassword={accessPassword}
                    teams={teams}
                    creatingTournament={creatingTournament}
                    onTournamentNameChange={setTournamentName}
                    onStyleChange={setSelectedStyle}
                    onVisibilityChange={setSelectedVisibility}
                    onAccessPasswordChange={setAccessPassword}
                    onAddTeam={addTeam}
                    onRemoveTeam={removeTeam}
                    onUpdateTeamName={updateTeamName}
                    onAddParticipant={addParticipant}
                    onUpdateParticipant={updateParticipant}
                    onRemoveParticipant={removeParticipant}
                    onSubmit={handleCreateTournament}
                    screens={screens}
                />
            </section>
        </div>
    );
};

export default TournamentManagerPage;
